import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { beginSignIn, browser } from "../shell/provider";
import { useProviderDoor } from "./doors";
import { Loading } from "../ui";

/** Where an identity link lands (ADR-0182 decisions 2a and 3c; #1034).
 * `zenith-api identity link --email …` prints
 * `https://<host>/auth/identity-link#<token>`: the token is the **fragment**,
 * which a browser never sends to a server, so it reaches Zenith only in the
 * bodies this page posts. */
export const IDENTITY_LINK_PATH = "/auth/identity-link";

/** Restates ADR-0182 decision 3c — the one refusal, naming nothing (§6.3). */
export const IDENTITY_LINK_REFUSED = "This sign-in cannot be linked with that link. The link may have been used already or expired, or you may have signed in with an account whose address is not the one it was issued for. Ask whoever holds the server for another, and sign in with that account.";

export const IDENTITY_LINK_NO_TOKEN = "This link is missing its token. Open the whole link exactly as it was printed.";

/** The staff sign-in is named by whose it is, the platform organization's
 * (ADR-0164's name for the marked organization), and never by the company:
 * this page answers before the sign-in gate, so anybody can reach it (#1678,
 * ruled 2026-10-12). */
export const IDENTITY_LINK_DONE = "Linked. Sign in with the platform organization's staff sign-in to continue.";

/** What the provider's return hands this page: the ID token and the link's
 * token, **in memory**, set by the callback just before it navigates here and
 * cleared once used. Not the history entry and not storage: an ID token is not left
 * behind in this browser for a reload or a back button to find. A reload
 * finds neither the handoff nor the fragment and says to open the link again,
 * which still works: only the confirm spends it. */
let handoff: { idToken: string; claim: string } | undefined;

export function handOffClaim(idToken: string, claim: string) {
  handoff = { idToken, claim };
}

/** Read without clearing: React may call an initializer twice in
 * development, and the second call must see what the first did. The check
 * below clears it once it has been used. */
function peekHandoff() {
  return handoff;
}

function fragment(): string {
  return window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
}

/** The address an ID token names, from its own claims — **for display only**.
 * The token is verified once, by the spend, which refuses one whose verified
 * address is not the user's (ADR-0182 decision 3c); nothing here decides. */
export function addressIn(idToken: string): string {
  try {
    const part = idToken.split(".")[1] ?? "";
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { email?: unknown };
    return typeof claims.email === "string" ? claims.email : "";
  } catch {
    return "";
  }
}

type Stage =
  | { kind: "reading" }
  | { kind: "start"; claim: string; user: string }
  | { kind: "checking" }
  | { kind: "confirm"; idToken: string; claim: string; user: string; provider: string }
  | { kind: "done" }
  | { kind: "refused" }
  | { kind: "no-token" }
  | { kind: "unavailable" };

/** The page. It answers before the sign-in gate, whatever the session: the
 * person spending a link may hold no credential at all yet — it is how the
 * first superuser gets one. It opens no session. */
export default function IdentityLink() {
  // Both read in initializers, once: the handoff is taken as it is read, and
  // the fragment before the effect below strips the address.
  const [returned] = useState(peekHandoff);
  const [claimAtLoad] = useState(fragment);
  const [stage, setStage] = useState<Stage>(() => {
    if (returned) return { kind: "checking" };
    return claimAtLoad ? { kind: "reading" } : { kind: "no-token" };
  });
  const read = useRef(false);
  const [busy, setBusy] = useState(false);
  const provider = useProviderDoor();
  const checked = useRef(false);

  useEffect(() => {
    // The token out of the address bar and the history entry at once.
    window.history.replaceState(window.history.state, "", IDENTITY_LINK_PATH);
  }, []);

  // Arriving from the link: say which user it is for, so the person knows
  // which account to sign in with before they go.
  useEffect(() => {
    if (stage.kind !== "reading" || read.current) return;
    read.current = true;
    void (async () => {
      const { data, response } = await api.POST("/auth/identity-link:preview", { body: { token: claimAtLoad } });
      if (!response.ok || !data) { setStage({ kind: "refused" }); return; }
      setStage({ kind: "start", claim: claimAtLoad, user: data.user_email });
    })();
  }, [stage.kind, claimAtLoad]);

  // Back from the provider: both addresses, before anything is bound. The
  // user's is read again because the link is not spent until the confirm.
  useEffect(() => {
    if (stage.kind !== "checking" || checked.current || !returned) return;
    checked.current = true;
    handoff = undefined;
    void (async () => {
      const { data, response } = await api.POST("/auth/identity-link:preview", { body: { token: returned.claim } });
      if (!response.ok || !data) { setStage({ kind: "refused" }); return; }
      setStage({ kind: "confirm", idToken: returned.idToken, claim: returned.claim, user: data.user_email, provider: addressIn(returned.idToken) });
    })();
  }, [stage.kind, returned]);

  const start = async (claim: string) => {
    if (!provider) { setStage({ kind: "unavailable" }); return; }
    setBusy(true);
    try {
      browser.assign(await beginSignIn(provider, IDENTITY_LINK_PATH, "claim", claim));
    } catch {
      setStage({ kind: "unavailable" });
      setBusy(false);
    }
  };

  const confirm = async (s: Extract<Stage, { kind: "confirm" }>) => {
    setBusy(true);
    const { response } = await api.POST("/auth/identity-link", { body: { token: s.claim, id_token: s.idToken } });
    setBusy(false);
    setStage(response.ok ? { kind: "done" } : { kind: "refused" });
  };

  if (stage.kind === "checking" || stage.kind === "reading") return <main className="gate"><Loading label="Checking the link" /></main>;
  return (
    <main className="gate">
      <div className="gate-card form">
        <div>
          <h1>Link your staff sign-in</h1>
          <p className="muted">
            This link was issued from the server so you can sign in to this console through the platform organization&apos;s staff sign-in.
            It works once.
          </p>
        </div>
        {stage.kind === "start" && (
          <>
            <p>This link is for <strong>{stage.user}</strong>. Sign in with that account at the staff sign-in.</p>
            <div className="actions">
            <button type="button" className="btn btn-primary" disabled={busy || !provider} onClick={() => void start(stage.claim)}>
              Continue with the staff sign-in
            </button>
            </div>
          </>
        )}
        {stage.kind === "confirm" && (
          <>
            <dl className="kv">
              <dt>You signed in as</dt><dd>{stage.provider || "an account whose address the sign-in did not name"}</dd>
              <dt>It will be linked to</dt><dd>{stage.user}</dd>
            </dl>
            <p className="muted">If that is not your account, do not confirm: close this page and start again from the link.</p>
            <div className="actions">
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void confirm(stage)}>Link this sign-in</button>
            </div>
          </>
        )}
        {stage.kind === "done" && (
          <>
            <div role="status" className="notice notice-good"><strong>{IDENTITY_LINK_DONE}</strong></div>
            <div className="actions"><a className="btn btn-primary" href="/">Go to sign-in</a></div>
          </>
        )}
        {stage.kind === "refused" && <div role="alert" className="problem"><div><strong>{IDENTITY_LINK_REFUSED}</strong></div></div>}
        {stage.kind === "no-token" && <div role="alert" className="problem"><div><strong>{IDENTITY_LINK_NO_TOKEN}</strong></div></div>}
        {stage.kind === "unavailable" && <div role="alert" className="problem"><div><strong>The staff sign-in is not available right now. Try again shortly.</strong></div></div>}
      </div>
    </main>
  );
}
